import { useQuery } from "@apollo/client";
import {
  Divider,
  Fab,
  makeStyles,
  Paper,
  Tooltip,
  Typography,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import { Skeleton } from "@material-ui/lab";
import moment from "moment";
import { useSnackbar } from "notistack";
import React from "react";
import { useParams } from "react-router";
import EmployeeDialog from "../../components/Employee/EmployeeDialog";
import LoadingError from "../../components/LoadingError";
import TextBox from "../../components/TextBox";
import { IdentifierMapping } from "../../constants";
import { GET_EMPLOYEE } from "../../graphql/queries";
import { GetEmployeeQuery } from "../../graphql/queries/getEmployee";
import { GetEmployeeQueryVariables } from "../../graphql/types";
import useSiteTitle from "../../hooks/useSiteTitle";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },

  contentRoot: {
    display: "flex",
    flex: 1,
    padding: "40px 60px",
    width: "100%",
    flexDirection: "column",
    marginTop: 40,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  detailsHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",

    flex: 1,
    justifyContent: "space-between",
    paddingBottom: theme.spacing(2),
  },
  content: {
    marginBottom: theme.spacing(2),
  },
  address: {
    marginTop: 20,
    paddingLeft: 10,
  },
}));
const EmployeeDetails: React.FC = () => {
  const classes = useStyles();
  const [showDialog, setShowDialog] = React.useState<string>("");

  const { locationId, identifier } =
    useParams<{
      locationId: string;
      identifier: string;
    }>();
  const { enqueueSnackbar } = useSnackbar();
  const { data, loading, error } = useQuery<
    GetEmployeeQuery,
    GetEmployeeQueryVariables
  >(GET_EMPLOYEE, {
    notifyOnNetworkStatusChange: true,
    variables: {
      locationId,
      identifier,
    },
    onError(error) {
      console.log(error);
      enqueueSnackbar("Fehler beim laden der Testpersonendaten", {
        variant: "error",
      });
    },
  });
  const { getEmployee } = data || {};
  const {
    patient: { firstname = "", lastname = "", email = "" } = {},
    location: { locationId: idOfLocation = "", name: locationName = "" } = {},
    identifier: identifierType = "insurance",
    insuranceNumber,
    address,
    birthdate,
    expiresAt = 0,
  } = getEmployee || {};

  const expireation = expiresAt * 1000;
  useSiteTitle(`Testpersonendaten - ${firstname} ${lastname}`);
  const _handleCloseDialog = () => {
    setShowDialog("");
  };
  const _openDialog = () => {
    setShowDialog(identifier);
  };
  return (
    <div className={classes.root}>
      <EmployeeDialog
        locationId={locationId}
        id={showDialog}
        open={!!showDialog}
        handleClose={_handleCloseDialog}
        initialValues={{
          insuranceNumber: identifier,
          identifier: identifierType || "insurance",
          patient: {
            firstname,
            lastname,
            email,
          },

          address,
          birthdate,
        }}
      />

      <div className={classes.header}>
        <Typography variant="h3">
          {!loading && `Personendaten für ${firstname} ${lastname}`}
          {loading && <Skeleton variant="text" width={700} height={80} />}
        </Typography>
      </div>
      {!loading && !!error && (
        <LoadingError text="Der Auftraggeber konnte nicht geladen werden" />
      )}
      {!error && loading && (
        <>
          <Skeleton variant="rect" height={600} style={{ marginTop: 20 }} />
        </>
      )}
      {!loading && !error && (
        <Paper className={classes.contentRoot}>
          <div className={classes.detailsHeader}>
            <Typography variant="h5">Details</Typography>
            <Tooltip title="Testpersonendaten bearbeiten">
              <Fab
                aria-label="update employee"
                size={"small"}
                disabled={loading}
                onClick={_openDialog}
                color={"primary"}
              >
                <EditIcon />
              </Fab>
            </Tooltip>
          </div>
          <Divider />
          <div className={classes.content}>
            <div className={classes.address}>
              <TextBox
                label={IdentifierMapping[identifierType || "insurance"]}
                value={`${insuranceNumber}`}
                bordered={false}
              />
              <TextBox
                label="Name"
                value={`${firstname} ${lastname}` || "-"}
                bordered={false}
              />
              <TextBox
                label="Standort"
                value={locationName || "-"}
                bordered={false}
              />
              <TextBox
                label="Standort-ID"
                value={idOfLocation || "-"}
                bordered={false}
              />
              <TextBox label="Email" value={email || "-"} bordered={false} />
              <TextBox
                label="Geburtsdatum"
                value={birthdate ? moment(birthdate).format("LL") : "-"}
                bordered={false}
              />
              {!!expireation && (
                <TextBox
                  label="Läuft ab am"
                  value={moment(expireation).format("LLL")}
                  bordered={false}
                />
              )}
              <Typography variant="overline">Adresse</Typography>
              <Divider />
              {address ? (
                <>
                  <TextBox
                    label="Straße "
                    value={`${address?.street || "-"} ${address?.number || ""}`}
                    bordered={false}
                  />
                  <TextBox
                    label="Ort und PLZ "
                    value={`${address?.city || "-"} ${address?.zip || "-"}`}
                    bordered={false}
                  />
                </>
              ) : (
                <Typography variant="body1">
                  Keine Adresse hinterlegt
                </Typography>
              )}
            </div>
          </div>
        </Paper>
      )}
    </div>
  );
};

export default EmployeeDetails;
